// Finding a match to join.
//
// THE ROUTE LOBBY FALLBACK IS THE DEFAULT PATH, NOT AN ERROR PATH. Riders
// board along a route rather than all at the terminal, so a trip-scoped lobby
// is usually empty for the first few minutes of a journey. A rider must never
// be shown — or dropped into — a route-labelled room that cannot fill, so:
//
//   1. Ask for lobbies on THIS trip.
//   2. If that comes back below the minimum worth joining, ask for the global
//      public pool, SILENTLY. No message, no "no one on your route yet".
//   3. If the pool is empty too, create a public lobby. The server keeps a
//      seeded pool for exactly this reason, so step 3 is rare.
//
// A match needs 2+ humans before it produces any competitive result at all, so
// the server's join list already floats lobbies holding a real player to the
// top. Joining the first entry is therefore joining the fullest useful room,
// not an arbitrary one.

import type { Client, Session } from '@heroiclabs/nakama-js';

/**
 * A lobby needs at least this many joinable seats' worth of interest before a
 * trip-scoped list is preferred over the global pool.
 *
 * One is enough: a single trip-scoped lobby means at least one other rider on
 * this vehicle is already waiting, which is the entire point of Route Lobby.
 */
const MIN_TRIP_LOBBIES = 1;

/** Rounds requested for a lobby we create. Server bound is [5, 26]. */
const DEFAULT_ROUNDS = 5;

/** Categories a match needs. Server requires EXACTLY this many, distinct. */
const REQUIRED_CATEGORIES = 4;

interface MatchListEntry {
  readonly match_id?: string;
  readonly label?: string;
}

export interface FoundMatch {
  readonly matchId: string;
  /** Where it came from, for analytics. Never shown to the player. */
  readonly source: 'trip' | 'pool' | 'created';
}

export interface FindMatchOptions {
  readonly partner?: string | null;
  readonly trip?: string | null;
}

/** Resolves a match id to join, creating one only as a last resort. */
export async function findMatch(
  client: Client,
  session: Session,
  opts: FindMatchOptions = {},
): Promise<FoundMatch> {
  if (opts.partner && opts.trip) {
    const onThisTrip = await listPublicMatches(client, session, {
      partner: opts.partner,
      trip: opts.trip,
    });
    if (onThisTrip.length >= MIN_TRIP_LOBBIES) {
      const id = firstId(onThisTrip);
      if (id) return { matchId: id, source: 'trip' };
    }
  }

  const pool = await listPublicMatches(client, session, {});
  const pooled = firstId(pool);
  if (pooled) return { matchId: pooled, source: 'pool' };

  return { matchId: await createMatch(client, session, opts), source: 'created' };
}

async function listPublicMatches(
  client: Client,
  session: Session,
  filter: { partner?: string; trip?: string },
): Promise<MatchListEntry[]> {
  try {
    const response = await client.rpc(session, 'list_public_matches', {
      count: 20,
      page: 0,
      ...filter,
    });
    const payload = asObject(response.payload);
    const matches = payload['matches'];
    return Array.isArray(matches) ? (matches as MatchListEntry[]) : [];
  } catch (err) {
    // A failed list is not a failed launch: fall through to the next strategy
    // rather than showing the rider an error they cannot act on.
    console.warn('[lobby] list_public_matches failed', filter, err);
    return [];
  }
}

/**
 * Creates a public lobby.
 *
 * Categories come from the server's own catalogue rather than a bundled list,
 * because the catalogue the client offers and the one the server SCORES
 * against are separate things that drift — a category the server does not know
 * makes every answer in it fall through to the costlier LLM check. Asking the
 * server removes the drift entirely for this client.
 */
async function createMatch(
  client: Client,
  session: Session,
  opts: FindMatchOptions,
): Promise<string> {
  const categories = await pickCategories(client, session);

  const response = await client.rpc(session, 'create_match', {
    categories,
    rounds: DEFAULT_ROUNDS,
    difficulty: 1,
    isPublic: true,
    host: session.user_id ?? '',
    classroomName: '',
    classroomEmblem: '',
    // `partner` in this payload is IGNORED by the server, which derives it
    // from the session — see resolveRouteLobbyLabel. Sent anyway so a reader
    // of this call is not surprised that the lobby comes out labelled.
    ...(opts.trip ? { trip: opts.trip } : {}),
  });

  const payload = asObject(response.payload);
  const matchId = payload['matchId'] ?? payload['match_id'] ?? payload['matchID'];
  if (typeof matchId !== 'string' || matchId === '') {
    throw new Error('create_match returned no match id');
  }
  return matchId;
}

async function pickCategories(client: Client, session: Session): Promise<string[]> {
  const response = await client.rpc(session, 'list_categories', {});
  const payload = asObject(response.payload);
  // The catalogue is keyed by mode; `mp` is the multiplayer set. Falling back
  // to the top-level keys covers a server that returns a flat map.
  const mp = asObject(payload['mp']);
  const names = Object.keys(Object.keys(mp).length > 0 ? mp : payload);
  if (names.length < REQUIRED_CATEGORIES) {
    throw new Error(`list_categories returned only ${names.length} categories`);
  }
  return shuffle(names).slice(0, REQUIRED_CATEGORIES);
}

function firstId(entries: MatchListEntry[]): string | null {
  for (const entry of entries) {
    if (typeof entry.match_id === 'string' && entry.match_id !== '') return entry.match_id;
  }
  return null;
}

function asObject(raw: unknown): Record<string, unknown> {
  const value = typeof raw === 'string' ? safeParse(raw) : raw;
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function shuffle<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
